import { getDateTag, getLocale, getNumberTag, getT, getUnitSystem } from '../i18n';

/**
 * Megjelenítési formázók. A területet SEHOL ne formázd kézzel — mindig
 * a `formatArea()`-t hívd, különben szétcsúszik a mértékegység-logika.
 *
 * docs/03-jatekszabalyok.md → A terület megjelenítése
 */

/**
 * ⚠️ EZRES TAGOLÁS MINDEN SZÁMNÁL (Geri, GRUNDO #61): „123 456", és a
 * négyjegyűeknél is: „1 234". A hu-HU alapértelmezése csak ötjegyűtől tagol
 * (`minimumGroupingDigits: 2`), ezért `useGrouping: 'always'`. Régebbi
 * motoron, amely a szöveges értéket nem ismeri, igaznak számít — ott a
 * négyjegyű marad tagolatlan, más nem változik.
 */
const num = (opts?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat(getNumberTag(), { useGrouping: 'always' as unknown as boolean, ...opts });

const METERS_PER_MILE = 1609.344;
const METERS_PER_FOOT = 0.3048;
const MPS_TO_MPH = 3600 / METERS_PER_MILE;

const imperial = () => getUnitSystem() === 'imperial';

/**
 * Terület MINDIG km²-ben, három tizedesjeggyel.
 *   0,001 km²  ·  0,072 km²  ·  1,845 km²  ·  55,830 km²
 *
 * Miért nem m²? Mert a valós számok gyorsan hatjegyűek lesznek („143 104 m²"),
 * és egy hatjegyű szám nem mond semmit — nincs mihez viszonyítani. A km² a
 * térképen is értelmezhető nagyságrend.
 *
 * AMIT EZ ÁLDOZ: három tizedesjegy km²-ben ezer négyzetméteres felbontás, egy
 * hexagon viszont 307 m². Néhány mezőnyi terület ezért „0,000 km²"-ként
 * jelenik meg. Ez tudatos csere: a kezdő felhasználó pár mezője úgyis
 * jelentéktelen, a nagyságrend viszont az első pillanattól olvasható.
 */
export function formatArea(m2: number): string {
  return `${num({ minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(m2 / 1_000_000)} km²`;
}

/**
 * Jelvényküszöb a magyar létrafokon: `0,1 km²` · `1 000 km²` · `250 000 km²`.
 *
 * Nem mért terület, hanem kerek küszöb, ezért nincs három tizedes (GRUNDO
 * #61), de az ezres tagolás megvan (Geri, 2026-11-02). Mért területre
 * továbbra is a `formatArea()`.
 */
export function formatAreaThreshold(m2: number): string {
  return `${num({ maximumFractionDigits: 1 }).format(m2 / 1_000_000)} km²`;
}

/**
 * Haladás a területküszöb felé a kártya alján: `719 km² / 1 000 km²`.
 *
 * Egész km² (Geri, 2026-11-02) — a három tizedes itt zaj. A tört küszöbű
 * fokokon (0,1 és 0,5 km²) egy tizedes, különben „0 km² / 0 km²" lenne. A
 * jelenlegi érték lefelé kerekít, hogy a fokot ne mutassa elértnek előbb.
 */
export function formatAreaProgress(currentM2: number, thresholdM2: number): string {
  const digits = thresholdM2 >= 1_000_000 ? 0 : 1;
  const scale = 10 ** digits;
  const current = Math.floor((currentM2 / 1_000_000) * scale) / scale;
  const format = num({ minimumFractionDigits: digits, maximumFractionDigits: digits });
  return `${format.format(current)} km² / ${format.format(thresholdM2 / 1_000_000)} km²`;
}

/** Távküszöb a magyar létrafokon: `50 km` · `1 000 000 km`. */
export function formatDistanceThreshold(km: number): string {
  return `${num({ maximumFractionDigits: 0 }).format(km)} km`;
}

/** Előjeles területváltozás: `+0,840 km²` / `−0,021 km²` */
export function formatAreaDelta(m2: number): string {
  const sign = m2 > 0 ? '+' : m2 < 0 ? '−' : '';
  return `${sign}${formatArea(Math.abs(m2))}`;
}

/**
 * Mezőszám tömören: `847`, `1K`, `2,2K`, `14K`.
 *
 * MIÉRT KELL? A statisztikapanel három egymás melletti dobozból áll, és a
 * mezőszám a terület MELLÉ kerül. Egy `12 480` ott kiszorítaná a km²-t —
 * márpedig a kettő együtt mond valamit: mekkora és hány darabból.
 *
 * Ezer alatt pontos szám, mert ott a pontosság még olvasható és érdekes.
 * Fölötte egy tizedes: a `2,2K` ugyanolyan gyorsan megfogható, mint a `847`,
 * és nem tolja szét a dobozt. A tízezres tartományban a tizedes már zaj,
 * ezért ott elmarad.
 *
 * Milliós fokozat is van: egy Balaton-méretű grund ~1,95 millió mező, és az
 * `1950K` alakban olvashatatlan. `1,9M` — ennyi.
 */
export function formatCellCount(cells: number): string {
  const value = Math.max(0, Math.round(cells));
  if (value < 1000) return num({ maximumFractionDigits: 0 }).format(value);
  if (value < 10_000) {
    return `${num({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value / 1000)}K`;
  }
  if (value < 1_000_000) return `${num({ maximumFractionDigits: 0 }).format(value / 1000)}K`;
  return `${num({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value / 1_000_000)}M`;
}

export function formatGp(gp: number): string {
  return `${num({ maximumFractionDigits: 0 }).format(gp)} GP`;
}

/** Csak a szám, mértékegység nélkül — pl. a Home statisztikapanel kiemelt sorához. */
export function formatNumber(value: number): string {
  return num({ maximumFractionDigits: 0 }).format(value);
}

/** Százalék legfeljebb egy tizedesjeggyel. */
export function formatPercent(value: number): string {
  return `${num({ maximumFractionDigits: 1 }).format(value)}%`;
}

export function formatDistance(meters: number, unit: 'km' | 'mi' = imperial() ? 'mi' : 'km'): string {
  if (unit === 'mi') {
    return `${num({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(meters / METERS_PER_MILE)} mi`;
  }
  return `${num({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(meters / 1000)} km`;
}

/** Short navigation distances, rounded to meters or feet. */
export function formatShortDistance(meters: number): string {
  const value = imperial() ? meters / METERS_PER_FOOT : meters;
  return `${num({ maximumFractionDigits: 0 }).format(value)} ${imperial() ? 'ft' : 'm'}`;
}

/** Weather values remain Celsius and km/h in the API. */
export function formatTemperature(celsius: number): string {
  return `${num({ maximumFractionDigits: 0 }).format(imperial() ? celsius * 9 / 5 + 32 : celsius)} ${imperial() ? '°F' : '°C'}`;
}

export function formatWindSpeed(kph: number): string {
  return `${num({ maximumFractionDigits: 1 }).format(imperial() ? kph / (METERS_PER_MILE / 1000) : kph)} ${imperial() ? 'mph' : 'km/h'}`;
}

/** Route selection keeps its original one-decimal distance display. */
export function formatRouteDistance(meters: number): string {
  const value = meters / (imperial() ? METERS_PER_MILE : 1000);
  return `${num({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)} ${imperial() ? 'mi' : 'km'}`;
}

/** Elevation gain per distance; input remains meters per kilometer. */
export function formatElevationRate(metersPerKm: number): string {
  const value = imperial() ? metersPerKm * (METERS_PER_MILE / 1000) / METERS_PER_FOOT : metersPerKm;
  return `${num({ maximumFractionDigits: 0 }).format(value)} ${imperial() ? 'ft/mi' : 'm/km'}`;
}

/** Tempó másodperc/km-ből: `7:40` */
export function formatPace(secondsPerKm: number): string {
  if (!Number.isFinite(secondsPerKm) || secondsPerKm <= 0) return '--:--';
  const m = Math.floor(secondsPerKm / 60);
  const s = Math.round(secondsPerKm % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Pace in the user's unit system, with the unit: `7:40/km` or `12:20/mi`.
 * `formatPace` stays per-km for the per-km splits table.
 */
export function formatPaceWithUnit(secondsPerKm: number): string {
  if (!imperial()) return `${formatPace(secondsPerKm)}/km`;
  return `${formatPace(secondsPerKm * (METERS_PER_MILE / 1000))}/mi`;
}

/** Időtartam: `52:24` vagy `1:04:18` */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0');
  return h > 0
    ? `${h}:${mm}:${String(s).padStart(2, '0')}`
    : `${mm}:${String(s).padStart(2, '0')}`;
}

export function formatElevation(meters: number): string {
  const sign = meters > 0 ? '+' : '';
  if (imperial()) return `${sign}${num({ maximumFractionDigits: 0 }).format(meters / METERS_PER_FOOT)} ft`;
  return `${sign}${num({ maximumFractionDigits: 0 }).format(meters)} m`;
}

/** Emelkedés előjel nélkül, az aktivitás összesítőjéhez. */
export function formatElevationGain(meters: number): string {
  const value = Math.max(0, meters);
  if (imperial()) return `${num({ maximumFractionDigits: 0 }).format(value / METERS_PER_FOOT)} ft`;
  return `${num({ maximumFractionDigits: 0 }).format(value)} m`;
}

/** Szorzó magyar tizedesvesszővel: `×1,15` */
export function formatMultiplier(value: number): string {
  return `×${num({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}`;
}

/** `1×` … `5×` */
export function formatDefense(level: number): string {
  return `${level}×`;
}

/** Átlagsebesség km/h-ban: `21,4 km/h` — bringánál ez a beszélt mérték. */
export function formatSpeed(metersPerSecond: number): string {
  if (!Number.isFinite(metersPerSecond) || metersPerSecond <= 0) return '--';
  if (imperial()) return `${num({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(metersPerSecond * MPS_TO_MPH)} mph`;
  return `${num({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(metersPerSecond * 3.6)} km/h`;
}

/**
 * Sebesség a rögzítés képernyő nagy számához — az ÁLLÁS is mérés.
 *
 * A `formatSpeed` a nullát „nincs adat"-nak veszi, és `--`-t ad rá: egy kész
 * aktivitás átlagánál ez a helyes. Mérés közben viszont a 0,0 km/h VALÓDI
 * érték (állsz a lámpánál), és a felület sem lehet olyan, hogy minden
 * megállásnál eltűnik a legnagyobb szám a képernyőről.
 *
 * A mértékegység SZÁNDÉKOSAN a sztring része: a rögzítés panelen a sebesség
 * ugyanúgy „12,0 km/h" alakban áll, ahogy a megtett táv „1,55 km" — azonos
 * méretben és színben, a címke pedig alatta a mérőszám NEVE („sebesség").
 */
export function formatLiveSpeed(metersPerSecond: number | null): string {
  if (metersPerSecond === null || !Number.isFinite(metersPerSecond)) return '—';
  if (imperial()) {
    const mph = Math.max(0, metersPerSecond) * MPS_TO_MPH;
    return `${num({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(mph)} mph`;
  }
  const kmh = Math.max(0, metersPerSecond) * 3.6;
  return `${num({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(kmh)} km/h`;
}

/**
 * A mozgásforma fő tempó-mérőszáma.
 *
 * Futásnál és sétánál a TEMPÓ (perc/km) a beszélt mérték, bringánál a
 * SEBESSÉG (km/h). Ugyanaz az adat, de aki bringázik, annak a „3:00/km"
 * semmit nem mond — és fordítva.
 */
export function formatEffort(
  type: 'run' | 'walk' | 'ride',
  meters: number,
  seconds: number,
): { value: string; label: string } {
  const t = getT().effort;
  if (meters <= 0 || seconds <= 0) return { value: '--', label: type === 'ride' ? t.speed : t.pace };
  return type === 'ride'
    ? { value: formatSpeed(meters / seconds), label: t.speed }
    : { value: formatPaceWithUnit(seconds / (meters / 1000)), label: t.pace };
}

/**
 * „most", „12 perce", „3 órája", „tegnap", vagy dátum.
 *
 * ⚠️ A NAPHATÁRT NAPTÁRI NAPOKBÓL SZÁMOLJUK, nem eltelt órákból: egy tegnap
 * este 23:00-kor kezdett futás ma reggel 7-kor „8 órája" lenne, pedig a
 * felhasználó fejében egyértelműen tegnapi.
 *
 * ⚠️ A MAI BEJEGYZÉSEKEN VISZONT NEM „ma" ÁLL (Geri kérése, 2026-09-17). Egy
 * értesítéslistán, ahol minden sor alatt ugyanaz a „ma" szó áll, a sorrenden
 * kívül semmi nem mondja meg, mikor történtek — pedig épp az a kérdés, hogy
 * az imént vagy reggel. A naptári naphatáron belül tehát eltelt időt írunk,
 * azon túl marad a napos alak.
 */
export function formatRelativeDay(at: number): string {
  const t = getT().time;
  const now = Date.now();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date(now)) - startOfDay(new Date(at))) / 86_400_000);

  if (days <= 0) {
    /* Jövőbeli időbélyeg (óraeltérés a szerver és a készülék között) sosem
       kaphat negatív számot — az „most" mindkét irányban helytálló. */
    const elapsed = Math.max(0, now - at);
    if (elapsed < 60_000) return t.now;
    const minutes = Math.floor(elapsed / 60_000);
    if (minutes < 60) return t.minutesAgo(minutes);
    return t.hoursAgo(Math.floor(minutes / 60));
  }

  if (days === 1) return t.yesterday;
  if (days < 7) return t.daysAgo(days);
  return new Date(at).toLocaleDateString(getDateTag(), { month: 'short', day: 'numeric' });
}

/**
 * A FEED időbélyege (Geri, GRUNDO #59 → #61):
 *
 *   - 24 órán belül eltelt idő: „most", „12 perce", „23 órája";
 *   - 1 nap felett a hét napja és az óra: „péntek 12:34";
 *   - 1 hét felett teljes dátum: „2026.09.18. 14:56".
 *
 * ⚠️ ELTELT IDŐBŐL, nem naptári napból — a „tegnap" alakot ez a sor
 * szándékosan nem ismeri: a „péntek 12:34" pontosabb, és ugyanolyan rövid.
 * Az értesítéslista és a hozzászólások a `formatRelativeDay`-en maradnak.
 */
export function formatFeedTime(at: number, now = Date.now()): string {
  const t = getT().time;
  const elapsed = Math.max(0, now - at);
  if (elapsed < 60_000) return t.now;
  if (elapsed < 60 * 60_000) return t.minutesAgo(Math.floor(elapsed / 60_000));
  if (elapsed < 24 * 60 * 60_000) return t.hoursAgo(Math.floor(elapsed / (60 * 60_000)));

  const date = new Date(at);
  if (getLocale() !== 'hu') {
    /* English: the device region decides 12/24-hour time and date order. */
    const tag = getDateTag();
    if (elapsed < 7 * 24 * 60 * 60_000) {
      return new Intl.DateTimeFormat(tag, { weekday: 'long', hour: 'numeric', minute: '2-digit' }).format(date);
    }
    return new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  }
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  if (elapsed < 7 * 24 * 60 * 60_000) {
    const weekday = new Intl.DateTimeFormat('hu-HU', { weekday: 'long' }).format(date);
    return `${weekday} ${time}`;
  }
  return `${date.getFullYear()}.${pad2(date.getMonth() + 1)}.${pad2(date.getDate())}. ${time}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Az aktivitás KEZDETE ÉS VÉGE a részletezőn (Geri, GRUNDO #59 → #61):
 *
 *   - egy napon belül: „2026. szept. 17. 10:41 - 18:20";
 *   - napváltásnál a vég is kap dátumot: „2026. szept. 17. 10:41 - szept. 18. 18:20";
 *   - évváltásnál a vég éve is kiíródik.
 */
export function formatActivityRange(startedAt: number, endedAt: number): string {
  const start = new Date(startedAt);
  const end = new Date(Math.max(startedAt, endedAt));
  const endTime = formatClockTime(end);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (sameDay) return `${formatDateTime(startedAt)} - ${endTime}`;
  const endDate = end.toLocaleDateString(getDateTag(), {
    ...(start.getFullYear() === end.getFullYear() ? {} : { year: 'numeric' }),
    month: 'short',
    day: 'numeric',
  });
  return `${formatDateTime(startedAt)} - ${endDate} ${endTime}`;
}

function formatClockTime(at: Date): string {
  if (getLocale() === 'hu') return `${pad2(at.getHours())}:${pad2(at.getMinutes())}`;
  return at.toLocaleTimeString(getDateTag(), { hour: 'numeric', minute: '2-digit' });
}

/** Time of day only: `10:41` (hu), `10:41 AM` (en). */
export function formatClock(at: number): string {
  return formatClockTime(new Date(at));
}

/** Teljes dátum és időpont: `2026. aug. 15. 15:54` */
export function formatDateTime(at: number): string {
  return new Date(at).toLocaleString(getDateTag(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DAY_PART = [
  { until: 5, key: 'night' },
  { until: 10, key: 'morning' },
  { until: 14, key: 'forenoon' },
  { until: 18, key: 'afternoon' },
  { until: 22, key: 'evening' },
] as const;

/** Az angol rövid napnévből a szótárbeli napindex (0 = vasárnap). */
const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/**
 * Automatikus cím: „Délutáni bringázás".
 *
 * Az aktivitásnak lesz szerkeszthető címe, de amíg a felhasználó nem ad neki,
 * a napszak + mozgásforma sokkal használhatóbb, mint egy azonosító. Egy
 * listában így is meg lehet különböztetni a reggeli futást az estitől.
 *
 * ⚠️ NYOLC ÓRÁNÁL HOSSZABB aktivitásnál a napszak félrevezető — egy egész
 * napos túra nem „reggeli" —, ezért ott a BEFEJEZÉS napjának neve jön:
 * „Szombati bringázás".
 */
export function activityTitle(
  type: 'run' | 'walk' | 'ride', startedAt: number, durationS = 0,
  endedAt = startedAt, timeZone?: string,
  autoTitle = getT().activity.autoTitle,
): string {
  const t = autoTitle;
  if (durationS > 8 * 60 * 60) {
    const short = new Intl.DateTimeFormat('en-US', { weekday: 'short', ...(timeZone ? { timeZone } : {}) })
      .format(new Date(endedAt));
    const day = WEEKDAY_INDEX[short] ?? new Date(endedAt).getDay();
    return `${t.weekday(day)} ${t.movement[type]}`;
  }
  const hour = timeZone
    ? Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone }).format(new Date(startedAt)))
    : new Date(startedAt).getHours();
  const part = DAY_PART.find((p) => hour < p.until)?.key ?? 'night';
  return `${t[part]} ${t.movement[type]}`;
}
